import React, { useRef, useState, useEffect } from 'react';

export default function InventoryItemModal({
  showModal,
  selectedItem,
  selectedImage,
  modalTitle,
  modalPrice,
  modalQty,
  pageRows,
  gotoIndex,
  gotoRel,
  commitAll,
  setShowModal,
  setSelectedItem,
  setSelectedImage,
  setModalTitle,
  setModalPrice,
  setModalQty,
  // ← we still need these for the editor
}) {
  const backdropRef = useRef(null);
  const fullImageRef = useRef(null);
  const [regionBoxStyle, setRegionBoxStyle] = useState(null);

  // Helper to turn normalized region → pixel overlay
  const computeBoxPosition = (imgEl, region) => {
    if (!imgEl || !region) return null;

    const naturalW = region.image_width_px;
    const naturalH = region.image_height_px;
    const renderedW = imgEl.clientWidth;
    const renderedH = imgEl.clientHeight;

    if (!naturalW || !naturalH) return null;

    const scaleX = renderedW / naturalW;
    const scaleY = renderedH / naturalH;

    return {
      left: region.x * scaleX,
      top: region.y * scaleY,
      width: region.width * scaleX,
      height: region.height * scaleY,
    };
  };

  // Runs every time the big image loads or changes
  const handleImageLoad = () => {
    try {
      let region = selectedItem?.image_region;

      // Vision sometimes returns a JSON string instead of an object
      if (typeof region === 'string') {
        try {
          region = JSON.parse(region);
        } catch (e) {
          console.warn('Failed to parse image_region JSON string');
          region = null;
        }
      }

      // Reset overlay first
      setRegionBoxStyle(null);

      if (!region || !fullImageRef.current) return;

      const pos = computeBoxPosition(fullImageRef.current, region);
      if (pos) {
        setRegionBoxStyle({
          position: 'absolute',
          left: `${pos.left}px`,
          top: `${pos.top}px`,
          width: `${pos.width}px`,
          height: `${pos.height}px`,
          border: '3px solid #00ff00',
          borderRadius: '6px',
          pointerEvents: 'none',
          boxShadow: '0 0 12px rgba(0,255,0,0.7)',
          zIndex: 50,
        });
      }
    } catch (err) {
      console.error('Bounding box overlay error:', err);
    }
  };

  // Focus the backdrop so arrow keys work immediately
  useEffect(() => {
    if (showModal && backdropRef.current) {
      backdropRef.current.focus();
    }
  }, [showModal]);

  if (!showModal || !selectedItem) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-y-auto min-h-[100dvh] p-4"
      onClick={async () => {
        await commitAll();
        setShowModal(false);
        setSelectedImage('');
        setSelectedItem(null);
      }}
      role="dialog"
      aria-modal="true"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowLeft') gotoRel(-1);
        if (e.key === 'Right' || e.key === 'ArrowRight') gotoRel(1);
        if (e.key === 'Escape') {
          commitAll();
          setShowModal(false);
          setSelectedImage('');
          setSelectedItem(null);
        }
      }}
    >
      <div
        className="relative bg-transparent p-2 rounded-lg max-w-4xl max-h-full overflow-visible mr-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close × */}
        <button
          onClick={async () => {
            await commitAll();
            setShowModal(false);
            setSelectedImage('');
            setSelectedItem(null);
          }}
          className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100/80 hover:bg-gray-200 text-gray-700 text-2xl font-bold z-10"
          aria-label="Close"
        >
          ×
        </button>

        {/* IMAGE + REGION OVERLAY */}
        <div className="flex justify-center items-center w-full">
          <div className="relative inline-block">
            <img
              ref={fullImageRef}
              src={selectedImage}
              key={selectedItem?.id}
              alt="Full size item"
              className="block max-w-full max-h-[75vh] object-contain"
              onLoad={handleImageLoad}
            />
            {regionBoxStyle && (
              <div
                className="absolute pointer-events-none"
                style={regionBoxStyle}
              />
            )}
          </div>
        </div>

        {/* Prev / Next arrows */}
        <button
          onClick={() => gotoRel(-1)}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-gray-100/80 hover:bg-gray-200 text-gray-800 text-3xl font-bold"
        >
          ‹
        </button>
        <button
          onClick={() => gotoRel(1)}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-gray-100/80 hover:bg-gray-200 text-gray-800 text-3xl font-bold"
        >
          ›
        </button>

        {/* Position indicator */}
        <div className="absolute top-4 left-4 rounded-md bg-black/70 text-white text-sm px-3 py-1.5">
          {pageRows.findIndex(r => r.id === selectedItem.id) + 1} / {pageRows.length}
        </div>

        {/* EDITOR CARD BELOW IMAGE */}
        <div className="absolute left-1/2 top-full -translate-x-1/2 -translate-y-3/4 z-60 pointer-events-auto">
          <div className="mx-auto w-[340px] max-w-[calc(100vw-32px)]">
            <div className="rounded-lg bg-black/80 text-white p-4 shadow-2xl border border-white/20 backdrop-blur">
              <div className="flex flex-col gap-3">
                <div className="flex gap-3 items-end flex-nowrap">
                  {/* Title */}
                  <div className="flex-1 min-w-0">
                    <label className="text-xs font-medium text-white/80">Title</label>
                    <input
                      type="text"
                      value={modalTitle}
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalTitle(val);
                        setSelectedItem(prev => ({ ...prev, title: val }));
                      }}
                      className="w-full mt-1 px-3 py-2 text-xs rounded border border-gray-300 bg-white text-gray-900"
                    />
                  </div>

                  {/* Price */}
                  <div className="w-16">
                    <label className="text-xs font-medium text-white/80">Price</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*[.,]?[0-9]*"
                      value={modalPrice}
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalPrice(val);
                        setSelectedItem(prev => ({
                          ...prev,
                          per_item_cost_estimate: val === '' ? null : Number(val)
                        }));
                      }}
                      className="w-full mt-1 px-3 py-2 text-xs rounded border border-gray-300 bg-white text-gray-900"
                    />
                  </div>

                  {/* Quantity */}
                  <div className="w-12">
                    <label className="text-xs font-medium text-white/80">Qty</label>
                    <input
                      type="number"
                      min="1"
                      value={modalQty}
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalQty(val);
                        setSelectedItem(prev => ({
                          ...prev,
                          quantity_estimate: val === '' ? 1 : Number(val)
                        }));
                      }}
                      className="w-full mt-1 px-3 py-2 text-xs rounded border border-gray-300 bg-white text-gray-900"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Spacer so the editor doesn't get cut off */}
        <div aria-hidden="true" className="h-16" />
      </div>
    </div>
  );
}
